// Client-side accessor for the world data manifests served by apps/world at
// /world/<name>.json. Fetched once on boot and cached.

import { api, assetUrl, cardArtUrl } from "./lib/config.ts";
import { canonicalCode } from "./lib/cards.ts";
import type {
  MapsManifest,
  PartManifest,
  PortraitManifest,
  NpcsManifest,
  SoundManifest,
  TitleManifest,
  CautionManifest,
  CardPacksManifest,
  ChibiManifest,
  MapData,
  AvatarPartVariant,
} from "@ygoevo/yo2-data";

export interface WorldData {
  maps: MapsManifest;
  /** Enlarged "main" waist-up bust set (customization/menus), gender-keyed. */
  avatarsPortrait: PortraitManifest;
  /** 16-slot character-editor part set. */
  avatarsEditor: PartManifest;
  /** 2008 chibi paper-doll avatar system. */
  chibi: ChibiManifest;
  npcs: NpcsManifest;
  sound: SoundManifest;
  title: TitleManifest;
  caution: CautionManifest;
  cardpacks: CardPacksManifest;
}

let cache: WorldData | null = null;
let inflight: Promise<WorldData> | null = null;

export function loadWorldData(): Promise<WorldData> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  // Cache-bust during dev so a regenerated manifest is always picked up (the
  // browser would otherwise serve a stale copy it cached earlier).
  const v = Date.now();
  const get = <T>(name: string) =>
    fetch(api(`/world/${name}.json?v=${v}`), { cache: "no-store" }).then((r) => r.json() as Promise<T>);
  inflight = Promise.all([
    get<MapsManifest>("maps"),
    get<PortraitManifest>("avatars-portrait"),
    get<PartManifest>("avatars-editor"),
    get<ChibiManifest>("chibi"),
    get<NpcsManifest>("npcs"),
    get<SoundManifest>("sound"),
    get<TitleManifest>("title"),
    get<CautionManifest>("caution"),
    get<CardPacksManifest>("cardpacks"),
  ]).then(
    ([maps, avatarsPortrait, avatarsEditor, chibi, npcs, sound, title, caution, cardpacks]) => {
      cache = { maps, avatarsPortrait, avatarsEditor, chibi, npcs, sound, title, caution, cardpacks };
      inflight = null;
      return cache;
    },
  );
  return inflight;
}

export function findMap(data: WorldData, id: string): MapData | undefined {
  return data.maps.maps.find((m) => m.id === id);
}

export function findVariant(
  set: PartManifest,
  slotId: number,
  variantId: number,
): AvatarPartVariant | undefined {
  return set.variants.find((v) => v.slotId === slotId && v.variantId === variantId);
}

// ── Asset overrides (Asset Creator) ────────────────────────────────────────
// A path → object-URL map consulted before the CDN. The community Asset Creator
// registers a creator's uploaded PNG under the *canonical* asset path the game
// renderer will request, so ChibiAvatar / PortraitAvatar / WorldRenderer — which
// all resolve art through asset() — composite the upload faithfully with zero
// renderer changes. Empty (and a no-op cost) for every normal screen.
const assetOverrides = new Map<string, string>();

/** Point a canonical asset path at a local object-URL (or clear it with null). */
export function setAssetOverride(path: string, url: string | null): void {
  if (url) assetOverrides.set(path, url);
  else assetOverrides.delete(path);
}

/** Drop every override (on Asset Creator teardown / reset). */
export function clearAssetOverrides(): void {
  assetOverrides.clear();
}

/** Map asset-relative path → fetchable URL. An Asset-Creator override wins, else
 *  dev: backend /assets (Vite proxy); prod: the R2 bucket origin (VITE_ASSET_BASE). */
export function asset(path: string): string {
  return assetOverrides.get(path) ?? assetUrl(path);
}

/** Self-hosted card image by Konami passcode (168px thumbnail). Served from our own R2/CDN in prod
 *  (see lib/config.ts cardArtUrl), we no longer hotlink ygoprodeck, which 429s under burst. The
 *  deck editor fetches card *metadata* via lib/cards.ts (TanStack Query); images load lazily and are
 *  browser-cached. */
export function cardImage(id: number): string {
  return cardArtUrl(canonicalCode(id), false);
}

/** Full-resolution self-hosted card image (for the large hover preview). */
export function cardImageLarge(id: number): string {
  return cardArtUrl(canonicalCode(id), true);
}

/** Standalone SQUARE card ARTWORK only (no frame/name/level), for composing our own pack/deck
 *  wrappers. In prod we go through our same-origin, edge-cached `/cardart/<id>?art=1` proxy so a
 *  grid of 6–8 covers can't burst-429 ygoprodeck from the eyeball IP (the failure that made covers
 *  fall back to the full framed card). In dev (no Pages Functions) we hotlink ygoprodeck directly.
 *  PackArt.svelte still falls back to the full card if even this 404s. */
export function cardArt(id: number): string {
  // Always the same-origin proxy: prod serves it from the Pages Function; dev reaches it through the
  // vite `/cardart` proxy (see vite.config.ts). Both return the edge-cached 1:1 crop, so covers never
  // fall back to the framed card the way a raw ygoprodeck hotlink does.
  return `/cardart/${id}?art=1`;
}

/** WebGL-texture-safe card art. The R2 origin sends GET * CORS (dev and prod alike), so we load it
 *  directly, three.js sets crossOrigin=anonymous by default, so the <canvas> texture isn't tainted. */
export function cardTextureUrl(id: number, big = false): string {
  return cardArtUrl(canonicalCode(id), big);
}

/** Lang-templated asset (e.g. title flash frames). */
export function langAsset(template: string, params: Record<string, string | number>): string {
  return asset(template.replace(/\{(\w+)\}/g, (_, k) => String(params[k] ?? "")));
}
